// @flow
import { tracker, PageLoadType, UserActionType } from '../../../tracking';
import { useCheckoutContext } from '../../../storeProviders/checkoutWidgetStore';
import { useEffect, useRef } from 'preact/hooks';

type Props = {
  lender_list?: string,
  lender_id?: string,
  lender_name?: string,
  merchant_origin?: string,
  merchant_name?: string,
  txn_amount?: number,
  tenure?: number,
  emi_amount?: number,
  aivf_type?: string,
  current_page?: string,
  button_label?: string,
  fail_reason?: string,
  error_code?: number,
  selected_cancel_reason?: string,
};

let prevPage;

export const useCheckoutTracker = (current_page?: string) => {
  const checkoutState = useCheckoutContext();
  const params = new URLSearchParams(window.location.search);
  const isInitial = params.get('initial');

  useEffect(() => {
    if (current_page) prevPage = current_page;
  }, [current_page]);

  const trackingPropsFromState: Props = {
    lender_list:
      checkoutState.eligibilityDetails &&
      Array.isArray(checkoutState.eligibilityDetails.emiLenderDetailsList)
        ? checkoutState.eligibilityDetails.emiLenderDetailsList.reduce(
            (acc, curr) => {
              if (!acc) return curr.lenderName;
              return acc + ',' + curr.lenderName;
            },
            '',
          )
        : undefined,
    lender_id:
      checkoutState.selectedLenderDetails &&
      checkoutState.selectedLenderDetails.lenderId,
    lender_name:
      checkoutState.selectedLenderDetails &&
      checkoutState.selectedLenderDetails.lenderName,
    merchant_origin:
      checkoutState.merchantData && checkoutState.merchantData.merchantOrigin,
    merchant_name:
      checkoutState.orderDetails && checkoutState.orderDetails.merchantName,
    txn_amount:
      checkoutState.orderDetails && checkoutState.orderDetails.orderAmount,
    tenure:
      checkoutState.selectedEmiPlanDetails &&
      checkoutState.selectedEmiPlanDetails.loanDuration,
    emi_amount:
      checkoutState.selectedEmiPlanDetails &&
      checkoutState.selectedEmiPlanDetails.effectiveMonthlyInstallment,
    aivf_type:
      checkoutState.selectedLenderDetails &&
      checkoutState.selectedLenderDetails.aivfDetails &&
      checkoutState.selectedLenderDetails.aivfDetails.aivfType,
    fail_reason:
      checkoutState.failureReason &&
      (checkoutState.failureReason.title || '') +
        (checkoutState.failureReason.message || ''),
    current_page: current_page ? current_page : prevPage,
  };

  const trackPageView = (pageKey: string, props?: Props) => {
    if (isInitial) return;
    return tracker.trackPageView(PageLoadType.DYNAMIC, pageKey, {
      ...trackingPropsFromState,
      ...props,
    });
  };

  const trackInputEntered = (
    elementName: string,
    pageKey: string,
    props: Props,
  ) => {
    if (isInitial) return;
    return tracker.trackUserInteraction(
      UserActionType.INPUT_ENTERED,
      elementName,
      pageKey,
      {
        ...trackingPropsFromState,
        ...props,
      },
    );
  };

  const trackClick = (elementName: string, pageKey?: string, props?: Props) => {
    if (isInitial) return;
    return tracker.trackUserInteraction(
      UserActionType.CLICK,
      elementName,
      pageKey,
      {
        ...trackingPropsFromState,
        ...props,
      },
    );
  };

  const trackAutomatic = (
    elementName: string,
    pageKey?: string,
    props?: Props,
  ) => {
    if (isInitial) return;
    return tracker.trackUserInteraction(
      UserActionType.AUTOMATIC,
      elementName,
      pageKey,
      {
        ...trackingPropsFromState,
        ...props,
      },
    );
  };

  const trackError = (elementName: string, pageKey?: string, props?: Props) => {
    if (isInitial) return;
    return tracker.trackApplicationError(elementName, pageKey, {
      ...trackingPropsFromState,
      ...props,
    });
  };

  return {
    trackPageView,
    trackInputEntered,
    trackClick,
    trackAutomatic,
    trackError,
    trackingPropsFromState,
  };
};



// WEBPACK FOOTER //
// ./components/checkoutWidget/tracker/index.js